import { ChevronDown, ChevronRight } from 'lucide-react';
import { useId, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { MediaRichTextEditor } from '@/features/media';

import { CtaFields } from '../cta-fields';
import { CustomFieldsEditor } from '../custom-fields-editor';
import { fieldText, type LooseForm } from '../field-text';
import { MediaRefField } from '../media-ref-field';

export interface BannerItemFieldsProps {
  /** `items.<n>` for a slide, `header` for a rail header. */
  name: string;
  storeId: string | null;
}

function GroupHeading({ children }: { children: string }) {
  return (
    <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
      {children}
    </p>
  );
}

/**
 * The fields every banner-shaped thing has, grouped: media, copy, buttons and, collapsed by
 * default, the custom bag.
 */
export function BannerItemFields({ name, storeId }: BannerItemFieldsProps) {
  const { t } = useTranslation('content');
  const form = useFormContext<LooseForm>();
  const advancedId = useId();
  const path = (field: string) => `${name}.${field}`;
  const [advancedOpen, setAdvancedOpen] = useState(() => {
    const custom = form.getValues(path('custom')) as unknown;
    return Array.isArray(custom) && custom.length > 0;
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <GroupHeading>{t('slide.groups.media')}</GroupHeading>
        <div className="grid gap-3 md:grid-cols-2">
          <MediaRefField
            name={path('media')}
            label={t('item.media')}
            description={t('item.mediaHint')}
          />
          <MediaRefField
            name={path('mobileMedia')}
            label={t('item.mobileMedia')}
            description={t('item.mobileMediaHint')}
          />
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <GroupHeading>{t('slide.groups.copy')}</GroupHeading>
        <div className="grid gap-3 md:grid-cols-2">
          <FormField
            control={form.control}
            name={path('eyebrow')}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('item.eyebrow')}</FormLabel>
                <FormControl>
                  <Input {...field} value={fieldText(field.value)} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={path('title')}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('item.title')}</FormLabel>
                <FormControl>
                  <Input {...field} value={fieldText(field.value)} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={path('subtitle')}
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>{t('item.subtitle')}</FormLabel>
                <FormControl>
                  <Input {...field} value={fieldText(field.value)} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={path('descriptionHtml')}
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>{t('item.description')}</FormLabel>
                <FormControl>
                  <MediaRichTextEditor
                    value={fieldText(field.value)}
                    onChange={(html) => field.onChange(html ?? '')}
                    onBlur={field.onBlur}
                    aria-label={t('item.description')}
                  />
                </FormControl>
                <FormDescription>{t('item.descriptionHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <GroupHeading>{t('slide.groups.buttons')}</GroupHeading>
        <CtaFields name={path('ctas')} storeId={storeId} />
      </div>
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground w-fit px-0 text-xs font-semibold tracking-wide uppercase hover:bg-transparent"
          aria-expanded={advancedOpen}
          aria-controls={advancedId}
          onClick={() => setAdvancedOpen((open) => !open)}
        >
          {advancedOpen ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
          {t('slide.groups.advanced')}
        </Button>
        {advancedOpen ? (
          <div id={advancedId}>
            <CustomFieldsEditor<LooseForm> name={path('custom')} label={t('custom.itemTitle')} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
